import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ButtonComponent } from "../../../../../shared/button/button.component";
import { NgForOf } from "@angular/common";
import { SharedModule } from "../../../../../shared/shared.module";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { FormControl, FormGroup } from "@angular/forms";
import {
  TrialNotificationExpression
} from "../../../../../core/models/trial-notification-template/trial-notification-template.model";
import { DiaryForm } from "../../../../../core/models/trial-diary-form/diary-form.model";
import { DiaryFormElementType } from "../../../../../core/constants/diary-form-element-type.enum";
import {
  TrialNotificationConditionComponent
} from "./trial-notification-condition/trial-notification-condition.component";
import { NotificationConditions } from "./notification-conditions.model";

@Component({
  selector: 'app-trial-notification-conditions',
  standalone: true,
  imports: [
    ButtonComponent,
    NgForOf,
    SharedModule,
    TrialNotificationConditionComponent
  ],
  templateUrl: './trial-notification-conditions.component.html',
  styleUrl: './trial-notification-conditions.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TrialNotificationConditionsComponent implements OnInit, OnChanges {
  @Input() diaryForm: DiaryForm;

  @Output() changed = new EventEmitter<NotificationConditions>();

  protected elementOptions: SelectOption[] = [];
  protected logicalOperationOptions: SelectOption[] = [
    new SelectOption('0', 'Logical Operator: AND'),
    new SelectOption('1', 'Logical Operator: OR')
  ];

  protected form = new FormGroup({
    id: new FormControl<string>(null),
    expressions: new FormControl<TrialNotificationExpression[]>([]),
    logicalOperator: new FormControl<string>('0')
  });

  ngOnInit() {
    this.buildElementOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['diaryForm'] && !changes['diaryForm'].firstChange) {
      this.buildElementOptions();
    }
  }

  private buildElementOptions() {
    this.elementOptions = [];
    if (!this.diaryForm) {
      return;
    }
    this.diaryForm.elements.forEach(element => {
      if (element.elementType === DiaryFormElementType.Dropdown || element.elementType === DiaryFormElementType.Radio || element.elementType === DiaryFormElementType.Checkbox) {
        this.elementOptions.push({value: element.id.toString(), text: element.label});
      }
    });
  }

  /**
   * Initialize the form with the provided conditions
   * @param conditions
   */
  init(conditions: NotificationConditions) {
    this.form.patchValue({
      logicalOperator: String(conditions.logicalOperator),
      expressions: []
    });

    conditions.expressions.forEach(expression => {
      this.form.get('expressions').value.push(new TrialNotificationExpression({
        elementId: expression.elementId,
        operator: expression.operator,
        value: expression.value
      }));
    })

    this.form.get('logicalOperator').valueChanges.subscribe(() => {
      this.onHandleNotifyChanges();
    });
  }

  conditionsValid() {
    if (!this.form.get('expressions').value)
      return false;

    // Return true only if every expression has values for the properties 'elementId', 'operator', and 'value'
    return this.form.get('expressions').value.every(expression => {
      return expression.elementId && expression.operator && expression.value;
    });
  }

  onHandleConditionChanged(expression: TrialNotificationExpression, index: number) {
    // Update the expression in the form
    this.form.get('expressions').value[index] = expression;

    this.onHandleNotifyChanges();
  }

  onHandleNotifyChanges() {
    if (!this.conditionsValid()) {
      this.changed.emit(new NotificationConditions({ logicalOperator: 0, expressions: [] }));
    } else {
      this.changed.emit(new NotificationConditions({
        logicalOperator: +this.form.get('logicalOperator').value,
        expressions: this.form.value.expressions.map(e => ({ elementId: e.elementId, operator: e.operator, value: e.value }))
      }));
    }
  }

  onAddCondition() {
    this.form.get('expressions').value.push(new TrialNotificationExpression());
    this.onHandleNotifyChanges();
  }

  onRemoveCondition(index: number) {
    this.form.get('expressions').value.splice(index, 1);
    this.onHandleNotifyChanges();
  }
}
